import { VoiceChatCredentials } from '../types/voice-chat';
import { apiClient } from './ApiService';
import {
  SharedSession,
  SharedSessionResponse,
  crewSharedSessionsParticipantsRegisterAuth,
  crewSharedSessionsRetrieve,
  crewSharedSessionsParticipantsRegisterAnonymous,
  crewSharedSessionsParticipantsList,
  crewSharedSessionsByOrganization,
  CrewSharedSessionsByOrganizationParams,
  SharedSessionParticipant,
  crewSharedSessionsParticipantsDeviceTelemetryList,
  CrewSharedSessionsParticipantsDeviceTelemetryListParams,
  TelemetryPoint
} from './types/api.generated';

/**
 * Service for managing SharedSession entities (Crew functionality)
 * Handles REST API operations for shared sessions
 */
export class CrewService {
  /**
   * List shared sessions by organization with optional filtering
   */
  static async listByOrganization(
    organizationId: string,
    params?: { enabled?: boolean; device_uuid?: string }
  ): Promise<SharedSession[] | undefined> {
    const queryParams: CrewSharedSessionsByOrganizationParams = {};

    if (params?.enabled !== undefined) {
      queryParams.status = params.enabled ? 'enabled' : 'disabled';
    }

    if (params?.device_uuid) {
      queryParams.device_uuid = params.device_uuid;
    }

    const response = await crewSharedSessionsByOrganization(organizationId, queryParams);

    if (response.status === 200) {
      return response.data.data;
    }
  }
  /**
   * Retrieve a specific shared session by ID
   */
  static async retrieve(id: string): Promise<SharedSessionResponse | undefined> {
    const response = await crewSharedSessionsRetrieve(id);

    if (response.status === 200) {
      return response.data.data;
    }
  }

  static async joinSharedSession(sessionId: string): Promise<SharedSessionParticipant | undefined> {
    const response = await crewSharedSessionsParticipantsRegisterAuth(sessionId, {});
    if (response.status === 200) {
      return response.data.data;
    }
  }

  static async joinSharedSessionAnonymous(
    sessionId: string,
    displayName: string
  ): Promise<SharedSessionParticipant | undefined> {
    const response = await crewSharedSessionsParticipantsRegisterAnonymous(sessionId, {
      display_name: displayName
    });
    if (response.status === 201) {
      return response.data.data;
    }
  }

  static async getParticipants(sessionId: string): Promise<SharedSessionParticipant[] | undefined> {
    const response = await crewSharedSessionsParticipantsList(sessionId);
    if (response.status === 200) {
      return response.data.data;
    }
  }

  /**
   * Fetch historic telemetry for a session/participant/device
   * Optimized to make the first query to get count, then create promise arrays
   * for parallel execution of remaining requests while maintaining order
   */
  static async getPastTelemetry(
    sharedSessionId: string,
    participantId: string,
    deviceUuid: string,
    params?: CrewSharedSessionsParticipantsDeviceTelemetryListParams
  ): Promise<TelemetryPoint[] | undefined> {
    const DEFAULT_LIMIT = 500; // Maximum allowed limit per request

    // Prepare base request parameters
    const firstParams: CrewSharedSessionsParticipantsDeviceTelemetryListParams = {
      ...params,
      limit: DEFAULT_LIMIT,
      offset: 0
    };

    const firstResponse = await crewSharedSessionsParticipantsDeviceTelemetryList(
      sharedSessionId,
      participantId,
      deviceUuid,
      firstParams
    );

    if (firstResponse.status !== 200) {
      return undefined;
    }

    const firstResponseData = firstResponse.data.data;
    const firstResults = firstResponseData.results || [];
    const totalCount = firstResponseData.count || 0;

    if (firstResults.length === 0 || totalCount === 0 || firstResults.length >= totalCount) {
      return firstResults;
    }

    const remainingCount = totalCount - firstResults.length;
    const limit = Math.max(DEFAULT_LIMIT, Math.ceil(remainingCount / 10));
    const baseParams: CrewSharedSessionsParticipantsDeviceTelemetryListParams = {
      ...params,
      limit,
      offset: 0
    };

    const additionalRequestsNeeded = Math.ceil(remainingCount / limit);
    const remainingPromises = Array.from({ length: additionalRequestsNeeded }, (_, i) => {
      const offset = firstResults.length + i * limit;

      if (offset >= totalCount) {
        return { offset, results: [] };
      }

      return crewSharedSessionsParticipantsDeviceTelemetryList(sharedSessionId, participantId, deviceUuid, {
        ...baseParams,
        offset
      })
        .then((response) => {
          if (response.status !== 200) {
            return { offset, results: [] };
          }

          const responseData = response.data.data;
          const results = responseData.results || [];

          return { offset, results };
        })
        .catch((error) => {
          console.error(`Failed to fetch telemetry at offset ${offset}:`, error);
          return { offset, results: [] };
        });
    });

    const remainingResponses = await Promise.all(remainingPromises);

    remainingResponses.sort((a, b) => a.offset - b.offset);

    const remainingResults = remainingResponses.flatMap(({ results }) => results);

    const allResults = [...firstResults, ...remainingResults];

    const finalResults = allResults.slice(0, totalCount);

    return finalResults;
  }

  /**
   * Fetch voice chat credentials for a participant in a shared session
   * Uses REST API endpoint instead of WebSocket
   */
  static async getVoiceChatCredentials(
    sharedSessionId: string,
    participantId: string
  ): Promise<VoiceChatCredentials | undefined> {
    try {
      const response = await apiClient.post<{
        data: {
          token: string;
          url: string;
          room_name: string;
          session_id: string;
          participant_id: string;
          provider: string;
        };
        message: string;
      }>(`/crew/shared-sessions/${sharedSessionId}/participants/${participantId}/voice-chat/credentials/`, {});

      if (response && response.data) {
        const { token, url, room_name, provider, session_id, participant_id } = response.data;
        return {
          token,
          url,
          roomName: room_name,
          provider,
          sessionId: session_id,
          participantId: participant_id
        };
      }
    } catch (error) {
      console.error('Failed to fetch voice chat credentials:', error);
      throw error;
    }
  }
}
